import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./NavOverlay.module.css";
import NavMenu, { type NavTab } from "../NavMenu/NavMenu";
import { useMenu } from "../../../context/MenuContext";
import { useScrollLock } from "../../../hooks/useScrollLock";

// 닫힘: 위 줄부터 아래 줄까지 순서대로 빨려들어감(마지막 항목 딜레이 0.165s + 0.17s) 먼저,
// 그 다음 오버레이 페이드(0.18s)
const CONTENT_EXIT_MS = 340;
const OVERLAY_EXIT_MS = 250; // 오버레이 페이드(0.18s) + 여유

// 진입 스태거 완료 시점(마지막 항목 딜레이 0.57s + duration 0.4s). 이 시점 이후엔
// NavMenu가 entering(0.4s+딜레이)에서 open(0.3s, 딜레이 없음) 상태로 바뀌어
// 이후의 호버 인터랙션이 진입 애니메이션의 딜레이를 물려받지 않게 함
const REVEAL_DONE_MS = 1000;

function getActiveTab(pathname: string): NavTab {
  if (pathname.startsWith("/product")) return "product";
  if (pathname.startsWith("/lens24")) return "lens24";
  if (pathname.startsWith("/place")) return "place";
  return "about";
}

export default function NavOverlay() {
  const { isOpen, closeMenu } = useMenu();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeTab = getActiveTab(pathname);

  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [closing, setClosing] = useState(false);

  useScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      setClosing(false);
      setRevealed(false);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setExpanded(true));
      });
      const revealTimer = window.setTimeout(() => setRevealed(true), REVEAL_DONE_MS);
      return () => {
        cancelAnimationFrame(id);
        window.clearTimeout(revealTimer);
      };
    }

    if (!mounted) return;

    // 1) 컨텐츠가 위로 빨려들어가듯 사라짐
    setClosing(true);
    setRevealed(false);
    const t1 = window.setTimeout(() => {
      // 2) 컨텐츠가 사라진 뒤 오버레이 배경 페이드아웃
      setExpanded(false);
    }, CONTENT_EXIT_MS);
    const t2 = window.setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, CONTENT_EXIT_MS + OVERLAY_EXIT_MS);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!mounted) return null;

  const handleSelect = (tab: NavTab) => {
    closeMenu();
    if (tab === "about") navigate("/");
    if (tab === "product") navigate("/product");
    if (tab === "lens24") navigate("/lens24");
    if (tab === "place") navigate("/place");
  };

  return (
    <div
      className={`${styles.overlay} ${expanded ? styles.expanded : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="메뉴"
    >
      <div className={styles.inner}>
        <div className={styles.headerSpacer} />
        <div className={styles.menuPanel}>
          <div className={styles.menuArea}>
            <NavMenu
              activeTab={activeTab}
              onSelect={handleSelect}
              expanded={expanded}
              revealed={revealed}
              closing={closing}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
